/**
 * WellnessCheckinSection
 *
 * Shared Well-Being Metrics section used in both:
 * - Personal Stats (HomeMaincontent)
 * - Admin Analytics (Analytics)
 */

import React, { useMemo, useState } from "react";
import { Alert, Box, Skeleton } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { LineChart } from "@mui/x-charts";
import { BarChart } from "@mui/x-charts/BarChart";

import useWellnessCheckin from "../../hooks/checkins/useWellnessCheckin";
import MonthRangeSelector from "../pearli/MonthRangeSelector";
import {
  getDefaultRangeMonthKeys,
  getRecentMonthOptions,
  monthKeyToLabel,
} from "../../utils/dateUtils";

const PREVIOUS_BAR_COLOR = "#3B82F6";
const CURRENT_BAR_COLOR = "#0C8F63";
const LINE_FILL_GREEN = "#19B880";
const NO_DATA_COLOR = "#CBD5E1";
const ACTIVE_BUTTON_LINE_COLOR = "#74E1B4";

// Temporary frontend-only graph demo. Set to false to resume API-backed graphs.
const USE_STATIC_CHECKIN_GRAPH_DATA = false;
const STATIC_CHECKIN_AVERAGES = {
  moodLevels: [3.2, 3.5, 3.8, 4.2],
  sleepQualityLevels: [3.0, 3.4, 3.9, 4.1],
  stressLevels: [3.8, 3.2, 2.7, 2.4],
  mindsetLevels: [3.1, 3.6, 4.0, 4.3],
  productivityLevels: [2.9, 3.3, 3.8, 4.2],
  creativityLevels: [3.3, 3.7, 4.1, 4.5],
};

function getStaticCheckinXAxis() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const day30Ago = new Date(now);
  day30Ago.setDate(now.getDate() - 29);

  const lengths = [7, 7, 7, 9];
  const buckets = [];
  let currStart = new Date(day30Ago);

  for (let i = 0; i < lengths.length; i++) {
    const currEnd = new Date(currStart);
    currEnd.setDate(currStart.getDate() + lengths[i] - 1);

    const startMonth = currStart.toLocaleDateString("en-US", { month: "short" });
    const endMonth = currEnd.toLocaleDateString("en-US", { month: "short" });

    let label = "";
    if (startMonth === endMonth) {
      label = `${startMonth} ${currStart.getDate()}-${currEnd.getDate()}`;
    } else {
      label = `${startMonth} ${currStart.getDate()} - ${endMonth} ${currEnd.getDate()}`;
    }

    buckets.push(label);
    currStart = new Date(currEnd);
    currStart.setDate(currEnd.getDate() + 1);
  }

  return buckets;
}

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 450,
    fontFamily: "Roboto",
    fontSize: theme.typography.pxToRem(15),
    border: "1px solid #dadde9",
  },
}));

const MORNING_METRICS = [
  {
    key: "moodLevels",
    label: "Mood Levels",
    tooltip:
      "Average mood levels scored from 1-5. Values of 0 indicate no data was collected for that interval.",
    color: ACTIVE_BUTTON_LINE_COLOR,
  },
  {
    key: "sleepQualityLevels",
    label: "Sleep Quality",
    tooltip:
      "Average sleep quality scored from 1-5. Values of 0 indicate no data was collected for that interval.",
    color: ACTIVE_BUTTON_LINE_COLOR,
  },
  {
    key: "stressLevels",
    label: "Stress Levels",
    tooltip:
      "Average stress levels scored from 1-5. Values of 0 indicate no data was collected for that interval.",
    color: ACTIVE_BUTTON_LINE_COLOR,
  },
];

const NIGHT_METRICS = [
  {
    key: "mindsetLevels",
    label: "Mindset Levels",
    tooltip:
      "Average mindset levels scored from 1-5. Values of 0 indicate no data was collected for that interval.",
    color: ACTIVE_BUTTON_LINE_COLOR,
  },
  {
    key: "productivityLevels",
    label: "Productivity Levels",
    tooltip:
      "Average productivity levels scored from 1-5. Values of 0 indicate no data was collected for that interval.",
    color: ACTIVE_BUTTON_LINE_COLOR,
  },
  {
    key: "creativityLevels",
    label: "Creativity Levels",
    tooltip:
      "Average creativity levels scored from 1-5. Values of 0 indicate no data was collected for that interval.",
    color: ACTIVE_BUTTON_LINE_COLOR,
  },
];

function getSeries(series, dayPart) {
  if (!series) return {};
  return series[dayPart] || {};
}

function getMetricAverage(series, dayPart, metricKey) {
  const daypartSeries = getSeries(series, dayPart);
  const values = daypartSeries[metricKey];
  if (!values || values.length === 0) return null;
  const valid = values.filter(
    (v) => v != null && Number.isFinite(Number(v)) && Number(v) > 0
  );
  if (valid.length === 0) return null;
  const sum = valid.reduce((acc, val) => acc + Number(val), 0);
  return Number((sum / valid.length).toFixed(2));
}

function formatValue(v) {
  return v != null ? v.toFixed(1) : "-";
}

function hasChartData(values) {
  return Array.isArray(values) && values.some((value) => Number(value) > 0);
}

function hasMetricValue(value) {
  return value != null && Number.isFinite(Number(value)) && Number(value) > 0;
}

function deltaColor(delta) {
  if (delta == null || Number.isNaN(delta)) return "#334155";
  if (delta > 0) return "#19B880";
  if (delta < 0) return "#EF4444";
  return "#334155";
}

function ActiveButton({ children, active, onClick }) {
  return (
    <div
      className={
        active
          ? "admin-section-interval-button-active"
          : "admin-section-interval-button"
      }
      onClick={onClick}
    >
      {children}
    </div>
  );
}

function smoothSeriesData(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  return arr.map((val) => {
    return val != null && Number.isFinite(Number(val)) && Number(val) > 0 ? Number(val) : null;
  });
}

function formatSubtitleRange(axis) {
  if (!Array.isArray(axis) || axis.length === 0) {
    return "Last 30 Days (4 Weeks)";
  }

  const formatItem = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item.trim();
    if (typeof item === "object") {
      if (typeof item.label === "string") return item.label.trim();
      if (item instanceof Date && !isNaN(item)) {
        return item.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }
    }
    return String(item).trim();
  };

  const firstStr = formatItem(axis[0]);
  const lastStr = formatItem(axis[axis.length - 1]);

  if (!firstStr && !lastStr) {
    return "Last 30 Days (4 Weeks)";
  }
  if (!firstStr) return lastStr;
  if (!lastStr) return firstStr;

  const startPart = firstStr.includes("-") ? firstStr.split("-")[0].trim() : firstStr;
  let endPart = lastStr.includes("-") ? lastStr.split("-").pop().trim() : lastStr;

  if (lastStr.includes("-")) {
    const startOfLast = lastStr.split("-")[0].trim();
    const month = startOfLast.split(" ")[0];
    if (month && !endPart.includes(month)) {
      endPart = `${month} ${endPart}`;
    }
  }

  return `${startPart} - ${endPart}`;
}

function WellnessLineChart({ data, xAxis, loading, color }) {
  const hasData = hasChartData(data);
  const chartColor = hasData ? color : NO_DATA_COLOR;
  const smoothedData = React.useMemo(() => smoothSeriesData(data || []), [data]);

  const xLabels = React.useMemo(() => {
    if (!Array.isArray(xAxis) || xAxis.length === 0) return ["W1", "W2", "W3", "W4"];
    return xAxis.map((item) => {
      if (typeof item === "string") return item;
      if (item && item.label) return item.label;
      if (item instanceof Date) {
        return item.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }
      return String(item);
    });
  }, [xAxis]);

  return loading ? (
    <Skeleton variant="rounded" width={300} height={222} />
  ) : (
    <Box sx={{ position: "relative", width: 300, height: 222 }}>
      <LineChart
        margin={{ top: 30, right: 25, bottom: 30, left: 35 }}
        xAxis={[
          {
            data: xLabels,
            scaleType: "point",
            tickLabelStyle: {
              fontSize: 10,
              fill: "#64748B",
            },
          },
        ]}
        yAxis={[
          {
            min: 1,
            max: 5,
            tickInterval: [1, 2, 3, 4, 5],
            tickMaxStep: 1,
            tickMinStep: 1,
            tickLabelStyle: {
              fontSize: 11,
              fontWeight: 500,
              fill: "#64748B",
            },
          },
        ]}
        series={[
          {
            data: smoothedData,
            valueFormatter: (value) =>
              value == null || !Number.isFinite(Number(value))
                ? "-"
                : Number(value).toFixed(2),
            connectNulls: true,
            curve: "monotoneX",
            color: chartColor,
            area: true,
            showMark: true,
          },
        ]}
        sx={{
          overflow: "visible",
          "& .MuiChartsSurface-root": {
            overflow: "visible",
          },
          "& .MuiChartsOverlay-root": {
            display: "none",
          },
          "& .MuiAreaElement-root": {
            fill: hasData ? chartColor : NO_DATA_COLOR,
          },
          "& .MuiMarkElement-root": {
            stroke: chartColor,
            fill: "#fff",
            strokeWidth: 2,
            r: 4,
          },
        }}
        width={300}
        height={222}
      />
      {!hasData && (
        <Box
          sx={{
            position: "absolute",
            top: 20,
            bottom: 40,
            left: 50,
            right: 15,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "#64748B",
            fontSize: 13,
            fontWeight: 500,
            pointerEvents: "none",
          }}
        >
          No data available for this period
        </Box>
      )}
    </Box>
  );
}

function WellnessRangeBarChart({
  prevValue,
  focusValue,
  prevLabel,
  focusLabel,
  loading,
}) {
  const xValues = [prevLabel, focusLabel];
  const prev =
    prevValue != null && Number.isFinite(Number(prevValue)) ? Number(prevValue) : 0;
  const focus =
    focusValue != null && Number.isFinite(Number(focusValue))
      ? Number(focusValue)
      : 0;
  const hasPrevData = hasMetricValue(prevValue);
  const hasFocusData = hasMetricValue(focusValue);
  const hasAnyData = hasPrevData || hasFocusData;
  const prevColor = hasPrevData ? PREVIOUS_BAR_COLOR : NO_DATA_COLOR;
  const focusColor = hasFocusData ? CURRENT_BAR_COLOR : NO_DATA_COLOR;

  return loading ? (
    <Skeleton variant="rounded" width={300} height={222} />
  ) : (
    <Box sx={{ width: 300 }}>
      <Box sx={{ position: "relative", width: 300, height: 222 }}>
        <BarChart
          xAxis={[
            {
              data: xValues,
              scaleType: "band",
            },
          ]}
          yAxis={[{ min: 0, max: 5, tickMaxStep: 1 }]}
          series={[
            {
              data: [prev, 0],
              color: prevColor,
              stack: "range",
            },
            {
              data: [0, focus],
              color: focusColor,
              stack: "range",
            },
          ]}
          width={300}
          height={222}
        />
      </Box>
      {(!hasPrevData || !hasFocusData) && (
        <Box
          sx={{
            mt: 0.5,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
            fontSize: 12,
            color: "#64748B",
            textAlign: "center",
            minHeight: 18,
            lineHeight: 1.2,
          }}
        >
          <span>{hasPrevData ? "" : `${prevLabel}: No data available`}</span>
          <span>{hasFocusData ? "" : `${focusLabel}: No data available`}</span>
        </Box>
      )}
    </Box>
  );
}

function WellnessComparisonTable({
  prevSeries,
  focusSeries,
  dayPart,
  prevLabel,
  focusLabel,
  loading,
}) {
  const metrics = dayPart === "morning" ? MORNING_METRICS : NIGHT_METRICS;

  return (
    <Box sx={{ mt: 2, width: "100%", overflowX: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr 1fr",
          gap: 1,
          px: 1,
          py: 0.5,
          background: "#F1F5F9",
          borderRadius: "6px 6px 0 0",
          fontWeight: 700,
          fontSize: 13,
          color: "#334155",
        }}
      >
        <span>Metric</span>
        <span>{prevLabel}</span>
        <span>{focusLabel}</span>
        <span>Change</span>
      </Box>

      {metrics.map(({ key, label }) => {
        const prev = getMetricAverage(prevSeries, dayPart, key);
        const focus = getMetricAverage(focusSeries, dayPart, key);
        const delta = prev != null && focus != null ? Number((focus - prev).toFixed(1)) : null;

        return (
          <Box
            key={key}
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr",
              gap: 1,
              px: 1,
              py: 1,
              borderBottom: "1px solid #E2E8F0",
              fontSize: 13,
              color: "#334155",
            }}
          >
            <span>{label}</span>
            <span>{loading ? "..." : formatValue(prev)}</span>
            <span>{loading ? "..." : formatValue(focus)}</span>
            <span style={{ color: deltaColor(delta), fontWeight: 600 }}>
              {loading ? "..." : delta != null ? `${delta > 0 ? "+" : ""}${delta}` : "-"}
            </span>
          </Box>
        );
      })}
    </Box>
  );
}

export default function WellnessCheckinSection({
  isAdmin = false,
  useStaticGraphData = USE_STATIC_CHECKIN_GRAPH_DATA,
}) {
  const { startKey: defaultPrev, endKey: defaultFocus } = getDefaultRangeMonthKeys();
  const monthOptions = useMemo(
    () => getRecentMonthOptions({ includeCurrentMonth: false }),
    []
  );

  const [viewMode, setViewMode] = useState("current");
  const [dayPart, setDayPart] = useState("morning");
  const [prevKey, setPrevKey] = useState(defaultPrev);
  const [focusKey, setFocusKey] = useState(defaultFocus);

  const { currentSeries, currentXAxis, prevSeries, focusSeries, loading, error } =
    useWellnessCheckin({
      isAdmin,
      viewMode,
      focusKey,
      prevKey,
      fetchEnabled: !useStaticGraphData,
    });

  const metrics = dayPart === "morning" ? MORNING_METRICS : NIGHT_METRICS;
  const useStaticCurrentGraphData = useStaticGraphData && viewMode === "current";
  const activeSeries = useStaticCurrentGraphData
    ? STATIC_CHECKIN_AVERAGES
    : getSeries(currentSeries, dayPart);
  const activeXAxis = useStaticCurrentGraphData
    ? getStaticCheckinXAxis()
    : currentXAxis;
  const prevLabel = monthKeyToLabel(prevKey);
  const focusLabel = monthKeyToLabel(focusKey);

  const sectionTitle = isAdmin ? "Employee Well-Being Metrics" : "Well-Being Metrics";
  const sectionSubtitle = isAdmin
    ? "Monitoring key wellness metrics across your organisation"
    : "Monitoring key wellness metrics";

  return (
    <div className="admin-section-group-container">
      <div className="admin-section-container">
        <div className="admin-section-header-container">
          <div className="admin-section-header">
            <div className="admin-section-text-container">
              <div className="admin-section-title">{sectionTitle}</div>
              <div className="admin-section-subtitle">{sectionSubtitle}</div>
            </div>

            <div className="admin-section-interval-button-container">
              <ActiveButton
                active={viewMode === "current"}
                onClick={() => setViewMode("current")}
              >
                Last 30 Days
              </ActiveButton>
              <ActiveButton
                active={viewMode === "range"}
                onClick={() => setViewMode("range")}
              >
                Month Range
              </ActiveButton>
            </div>
          </div>
        </div>

        <div className="admin-section-interval-button-container">
          <ActiveButton
            active={dayPart === "morning"}
            onClick={() => setDayPart("morning")}
          >
            Morning
          </ActiveButton>
          <ActiveButton active={dayPart === "night"} onClick={() => setDayPart("night")}>
            Night
          </ActiveButton>
        </div>

        {viewMode === "range" && (
          <div className="admin-range-selector">
            <MonthRangeSelector
              startMonth={prevKey}
              endMonth={focusKey}
              monthOptions={monthOptions}
              startLabel="From Month"
              endLabel="To Month"
              sx={{ mt: 0, mb: 1 }}
              onChangeStart={setPrevKey}
              onChangeEnd={setFocusKey}
            />
          </div>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1, mb: 1, fontSize: 12 }}>
            {error}
          </Alert>
        )}

        {viewMode === "current" && (
          <div className="admin-section-item-container">
            {metrics.map(({ key, label, tooltip, color }) => (
              <div className="admin-section-item" key={key}>
                <div className="admin-section-item-header">
                  <div className="admin-section-item-title">
                    {label}
                    <div className="admin-info-icon" style={{ marginLeft: "10vw" }}>
                      <HtmlTooltip arrow title={tooltip} placement="top">
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </HtmlTooltip>
                    </div>
                  </div>
                  <div className="admin-section-item-subtitle">
                    {formatSubtitleRange(activeXAxis)}
                  </div>
                  <div className="admin-section-item-body-container">
                    <WellnessLineChart
                      data={activeSeries[key] || []}
                      xAxis={activeXAxis}
                      loading={useStaticCurrentGraphData ? false : loading}
                      color={color}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {viewMode === "range" && (
          <>
            <div className="admin-section-item-container">
              {metrics.map(({ key, label, tooltip, color }) => {
                const prevValue = getMetricAverage(prevSeries, dayPart, key);
                const focusValue = getMetricAverage(focusSeries, dayPart, key);

                return (
                  <div className="admin-section-item" key={`range-${key}`}>
                    <div className="admin-section-item-header">
                      <div className="admin-section-item-title">
                        {label}
                        <div className="admin-info-icon" style={{ marginLeft: "10vw" }}>
                          <HtmlTooltip arrow title={tooltip} placement="top">
                            <FontAwesomeIcon icon={faInfoCircle} />
                          </HtmlTooltip>
                        </div>
                      </div>
                      <div className="admin-section-item-subtitle">{`${prevLabel} vs ${focusLabel}`}</div>
                      <div className="admin-section-item-body-container">
                        <WellnessRangeBarChart
                          prevValue={prevValue}
                          focusValue={focusValue}
                          prevLabel={prevLabel}
                          focusLabel={focusLabel}
                          loading={loading}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <WellnessComparisonTable
              prevSeries={prevSeries}
              focusSeries={focusSeries}
              dayPart={dayPart}
              prevLabel={prevLabel}
              focusLabel={focusLabel}
              loading={loading}
            />
          </>
        )}
      </div>
    </div>
  );
}
